import { useMemo } from 'react';
import _ from 'lodash';
import { buildSkipLogic } from '../../../tester/pages/survey/helpers';

/**
 * Custom hook to identify which survey pages have been skipped based on skip logic.
 *
 * @param {Array} surveyPages - An array of survey page objects.
 * @param {Array} answers - An array of answer objects provided by the participant.
 * @returns {Array} skippedPages - An array of skipped pages.
 */
const useIdentifySkippedPages = (surveyPages, answers) => useMemo(() => {
  const skippedPages = [];
  const orderedSurveyPages = _.sortBy(surveyPages, 'order');
  let currentPageIndex = 0;

  while (currentPageIndex < orderedSurveyPages.length) {
    const currentPage = orderedSurveyPages[currentPageIndex];
    // Apply buildSkipLogic to determine the next page
    const targetPageId = buildSkipLogic(answers, currentPage);

    if (targetPageId) {
      if (targetPageId === 'end_screen') {
        // Mark all subsequent pages as skipped
        const pagesToSkip = orderedSurveyPages.slice(currentPageIndex + 1);

        pagesToSkip.forEach((page) => {
          skippedPages.push({
            pageId: page.id,
            pageOrder: page.order,
            skippedPageMessage: 'This page was skipped due to skip logic rules.',
          });
        });
        break; // No more pages to process after end_screen
      } else {
        // Find the index of the target page
        const targetPageIndex = _.findIndex(orderedSurveyPages, { id: targetPageId });

        if (targetPageIndex === -1) {
          console.warn(`Target page ID ${targetPageId} not found.`);
          break; // Exit if target page is not found
        }

        // Mark all pages between current and target as skipped
        const pagesToSkip = orderedSurveyPages.slice(currentPageIndex + 1, targetPageIndex);
        pagesToSkip.forEach((page) => {
          skippedPages.push({
            pageId: page.id,
            pageOrder: page.order,
            skippedPageMessage: 'This page was skipped due to skip logic rules.',
          });
        });

        // Move to the target page
        currentPageIndex = targetPageIndex;
      }
    } else {
      // No skip logic applied, proceed to the next page
      currentPageIndex += 1;
    }
  }

  return skippedPages;
}, [surveyPages, answers]);

export default useIdentifySkippedPages;
